import { Box, Button, Divider, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { useQueryClient } from 'react-query';

import { ControlledTextField } from '@frontend/features/common/components/ControlledTextField';
import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { useResolver } from '../helpers';

interface FormProps {
  onClose: () => void;
  agencyId: string;
}

export const Form = ({ onClose, agencyId }: FormProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const resolver = useResolver();
  const methods = useForm({
    resolver,
  });
  const { handleSubmit, control } = methods;

  const onSubmit = async (data: { name: string }) => {
    setLoading(true);
    try {
      await client.post('/organizations', { ...data, agencyId: Number(agencyId) });
      addToast({
        message: t('organizations.create.createdSuccess'),
        color: 'success',
      });
      queryClient.invalidateQueries();
      onClose();
    } catch (e) {
      addToast({
        message: t('organizations.create.createdError'),
        color: 'error',
      });
    } finally {
      setLoading(false)
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, 1fr)"
          gap={2}
          sx={{
            maxHeight: 'calc(100vh - 300px)',
            overflowX: 'auto',
            paddingTop: theme.spacing(2),
          }}
        >
          <Box gridColumn="span 12">
            <ControlledTextField
              control={control}
              name="name"
              label={t('organizations.create.name')}
              placeholder={t('common.form.onlyLetters')}
              required
              errorMessage={t('common.form.required')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 12">
            <ControlledTextField
              control={control}
              name="domain"
              label={t('organizations.create.domain')}
              placeholder={t('common.form.onlyLetters')}
              required
              errorMessage={t('common.form.required')}
              fullWidth
            />
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" gap={theme.spacing(3)} paddingTop={theme.spacing(3)}>
          <Divider />
          <Box
            display="flex"
            justifyContent="space-evenly"
            width="100%"
            gap={theme.spacing(1)}
            sx={{
              '& > button': {
                width: 'calc(50% - 4px)',
              },
            }}
          >
            <Button variant="outlined" color="error" size="medium" onClick={onClose}>
              {t('common.form.discard')}
            </Button>
            <LoadingButton loading={loading} disabled={loading} variant="contained" color="primary" type="submit" size="medium">
              {t('common.form.save')}
            </LoadingButton>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};
